import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { fixedText, SageEntityTypeName, SyncTarget, syncTargetsFor } from '../../../../shared/services/sage-integration.model';
import { UnitFixView } from '../../../../shared/services/sage-unit-fix.model';
import { UnitGroupView } from '../../../../shared/services/sage-unit-group.model';
import { ErrorGroup, ErrorRecordRow } from '../dashboard/errors-column.component';
import { UnitFixPanelComponent } from '../units/unit-fix-panel.component';
import { CategoryFixComponent } from './category-fix.component';
import { CodeFixComponent } from './code-fix.component';
import { RefreshGroupsFixComponent } from './refresh-groups-fix.component';
import { RematchFixComponent } from './rematch-fix.component';
import { SageErrorFixStore } from './sage-error-fix.store';
import { SalesGroupFixComponent } from './sales-group-fix.component';
import { SupportFixComponent } from './support-fix.component';

/** One reason from "Errors you can fix": what to do, how it clears, the fix and its records. Shared by
 * the web panel and the phone sheet. */
@Component({
  selector: 'app-error-fix-panel',
  standalone: true,
  imports: [
    CommonModule,
    UnitFixPanelComponent,
    SalesGroupFixComponent,
    RefreshGroupsFixComponent,
    CodeFixComponent,
    CategoryFixComponent,
    RematchFixComponent,
    SupportFixComponent,
  ],
  templateUrl: './error-fix-panel.component.html',
  styleUrls: ['../onboarding-wizard/sage-wizard.shared.css', '../sage-mockup.shared.css', './error-fix-panel.component.css'],
})
export class ErrorFixPanelComponent {
  @Input({ required: true }) group!: ErrorGroup;
  @Input({ required: true }) store!: SageErrorFixStore;
  @Input() unitFix: UnitFixView | null = null;
  @Input() syncEnabled = true;
  @Input() idPrefix = '';
  /** A sync is running, from here or anywhere else. */
  @Input() syncBusy = false;
  /** The sync started from this panel while it runs: a record type, or 'all'. */
  @Input() syncing: string | null = null;
  /** The sync started from this panel has finished. */
  @Input() synced = false;
  @Input() groupsLastRead: string | null = null;

  @Output() action = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();
  @Output() sync = new EventEmitter<SyncTarget>();
  @Output() openMatching = new EventEmitter<SageEntityTypeName>();
  @Output() unitGroupChange = new EventEmitter<{ group: UnitGroupView; uomId: number }>();
  @Output() unitItemChange = new EventEmitter<{ matchId: number; uomId: number }>();
  @Output() unitApply = new EventEmitter<void>();

  readonly trackRecord = (_: number, r: ErrorRecordRow) => r.id;
  readonly trackTarget = (_: number, t: SyncTarget) => this.targetKey(t);
  readonly loadingUnits: UnitFixView = {
    groups: [],
    units: [],
    total: 0,
    applyCount: 0,
    loading: true,
    loadError: false,
    savingIds: new Set(),
    applying: false,
    error: null,
  };

  get showsUnits(): boolean {
    return this.group.fix === 'units' && !this.group.fixed;
  }

  /** The plain list, where the fix doesn't list the records itself. */
  get showsRecords(): boolean {
    const own: (string | null)[] = ['rematch', 'support', 'code', 'name', 'categories', 'sales-group'];
    return !this.group.fixed && !own.includes(this.group.fix);
  }

  /** "Category not brought in": the waiting items whose category is now in Aivor. */
  get categoryApply(): { label: string; disabled: boolean } | null {
    if (this.group.fixed || this.group.fix !== 'categories') return null;
    const state = this.store.categories;
    const n = state.readyMatchIds.length;
    if (!n) return null;
    return {
      label: state.applying ? 'Applying…' : `Apply and retry ${n} ${n === 1 ? 'item' : 'items'}`,
      disabled: state.applying || !this.syncEnabled,
    };
  }

  /** The dashboard holds the first page of problems, so a long reason may show only some. */
  get moreThanShown(): boolean {
    return this.group.records.length < this.group.count;
  }

  get fixedText(): string {
    return fixedText(this.group.fix, this.synced);
  }

  /** The buttons that leave the panel: the only place a bill of materials is made, or the tab that fixes it. */
  get mainLabel(): string | null {
    if (this.group.fixed) return null;
    switch (this.group.fix) {
      case 'bills-of-materials':
      case 'review':
      case 'matches':
        return this.group.action;
      default:
        return null;
    }
  }

  get syncTargets(): SyncTarget[] {
    if (this.synced) return [];
    const types = [...new Set(this.group.records.map((r) => r.entityType))];
    // A change saved here still has to reach Sage, and a code Sage already uses only clears on that sync.
    return syncTargetsFor(this.group.fix, types, !!this.group.fixed || this.store.changesSaved);
  }

  get syncHint(): string | null {
    if (!this.syncTargets.length) return null;
    if (!this.syncEnabled) return 'Sync is paused for this organisation.';
    if (this.syncBusy && !this.syncing) return 'Another sync is running. Try again when it finishes.';
    return null;
  }

  targetKey(t: SyncTarget): string {
    return t.type ?? 'all';
  }

  syncLabel(t: SyncTarget): string {
    return this.syncing === this.targetKey(t) ? t.label.replace(/^Sync (.+) now$/, 'Syncing $1…') : t.label;
  }

  /** A record's code, when its name doesn't already show it. */
  codeOf(record: ErrorRecordRow): string | null {
    return record.recordId && record.recordId !== record.name ? record.recordId : null;
  }
}
